import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  defaultEventEditorValues,
  type EventEditorFormValues,
} from "../components/event-form/event.scheme";
import type { EventDetailForAdminResponse, EventStatus } from "../types/event.types";
import {
  formatUTCToDateInTimeZone,
  formatUTCToTimeInTimeZone,
  getBrowserTimeZone,
} from "../../../../lib/date-time";

dayjs.extend(utc);
dayjs.extend(timezone);

const formatWithTimeZone = (
  dateString: string,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
) => {
  if (!dateString) return "";

  const date = dayjs.utc(dateString);
  if (!date.isValid()) return dateString;

  try {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: timeZone || "UTC",
    }).format(date.toDate());
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: "UTC",
    }).format(date.toDate());
  }
};

export const getEventStatus = (event: {
  event_date: string | null;
  start_time: string | null;
  end_time: string | null;
  event_status?: string | null;
}): EventStatus => {
  const now = new Date();
  const eventDate = dayjs.utc(event.event_date);
  const startTime = dayjs.utc(event.start_time);
  const endTime = dayjs.utc(event.end_time);

  if (!eventDate.isValid() || !startTime.isValid() || !endTime.isValid()) {
    if (
      event.event_status === "upcoming" ||
      event.event_status === "ongoing" ||
      event.event_status === "completed"
    ) {
      return event.event_status;
    }

    return "ongoing";
  }

  if (now < eventDate.toDate() && now < startTime.toDate()) {
    return "upcoming";
  }

  if (now > endTime.toDate()) {
    return "completed";
  }

  return "ongoing";
};

export const formatEventDate = (dateString: string, timeZone?: string | null) =>
  formatWithTimeZone(
    dateString,
    {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    },
    timeZone,
  );

export const formatEventTime = (timeString: string, timeZone?: string | null) =>
  formatWithTimeZone(
    timeString,
    {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
    timeZone,
  );

export const formatFullEventDateTime = (
  dateString: string,
  timeString: string,
  timeZone?: string | null,
) => {
  if (!dateString || !timeString) return "";
  const weekday = formatWithTimeZone(dateString, { weekday: "long" }, timeZone);
  const month = formatWithTimeZone(dateString, { month: "long" }, timeZone);
  const day = formatWithTimeZone(dateString, { day: "numeric" }, timeZone);
  const timeStr = formatEventTime(timeString, timeZone);
  return `${weekday}, ${month} ${day} at ${timeStr}`;
};

export const mapEventToFormValues = (
  viewEvent: EventDetailForAdminResponse,
): { values: EventEditorFormValues; loadedPoints: string } => {
  const eventTimeZone = viewEvent.time_zone || getBrowserTimeZone();
  const dateValue = formatUTCToDateInTimeZone(
    viewEvent.start_time || viewEvent.event_date || "",
    eventTimeZone,
  );
  const loadedPoints = viewEvent.points === null ? "" : String(viewEvent.points);
  return {
    values: {
      ...defaultEventEditorValues,
      imageUrl: viewEvent.image_url || "",
      name: viewEvent.title,
      description: viewEvent.description,
      location: viewEvent.location || "",
      lat: String(viewEvent.latitude ?? ""),
      lon: String(viewEvent.longitude ?? ""),
      radius: String(viewEvent.radius ?? ""),
      points: loadedPoints,
      eventDate: dateValue,
      start: formatUTCToTimeInTimeZone(viewEvent.start_time || "", eventTimeZone),
      end: formatUTCToTimeInTimeZone(viewEvent.end_time || "", eventTimeZone),
      checkInTime: formatUTCToTimeInTimeZone(viewEvent.check_in_time || "", eventTimeZone),
      guestAttendanceVerification: viewEvent.guest_attendance_verification || "",
      timezone: eventTimeZone,
      categoryId: viewEvent.category_id || "",
      subCategoryIds: viewEvent.interest_ids || [],
    },
    loadedPoints,
  };
};
